import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { youthPrograms as youthProgramsApi, youthGroups as youthGroupsApi } from '../../api/endpoints';
import {
  useMultiLedger, usePrint, useSearch, usePagination, useNotification, useOptionList, useDateRangeFilter, useMemberDirectory,
  useOpenRecordFromLink, findLedgerRow, RECORD_GONE
} from '../../hooks';
import { formatDate, formatCurrency, ageFrom } from '../../utils/format';
import { printableRows, printReference } from '../../utils/printRows';
import { pathForModuleId } from '../../routes';
import useStyles from '../../theme/useStyles';
import { missingRequired, requiredError } from '../../utils/requiredFields';
import {
  PageHeader, SubTabs, SearchBar, Pagination, Notification, PromptDialog, FormLayout, Input, Select, SearchableSelect, Badge, Button, TablePanel, Drawer, RowActions, DateRangeFilter, 
} from '../../components/ui';
import PrintButton from '../../components/ui/PrintButton';
import type { Member } from '../../types';
import YouthSummary from './YouthSummary';

// A fellowship is a rule — a name, an age band and a gender — and its people
// are whoever in the church register (Members & Families) fits the rule. There
// is no second list of youth to keep in step with the register: register a
// member once, and they appear in every fellowship they qualify for.

/** Shown when a legacy programme predates the fellowship field. */
const FELLOWSHIP_FALLBACK = 'Youth Fellowship';

const EMPTY_GROUP = { name: '', ageFrom: '', ageTo: '', gender: 'Both', notes: '' };
const GROUP_GENDERS = ['Both', 'Male', 'Female'];
const EMPTY_PROGRAM = {
  fellowshipName: '', date: '', title: '', progType: '', attendance: '', budget: '', notes: ''
};

type Rule = { ageFrom?: number | null; ageTo?: number | null; gender?: string };

/** "15 to 30 · Both", "18 and above · Female", "up to 14 · Both", "any age · Male" */
const ageBandOf = (g: Rule): string => {
  const from = g.ageFrom ?? null; const to = g.ageTo ?? null;
  return from !== null && to !== null ? `${from} to ${to}`
    : from !== null ? `${from} and above`
      : to !== null ? `up to ${to}` : 'any age';
};
const ruleOf = (g: Rule): string => `${ageBandOf(g)} · ${g.gender || 'Both'}`;

/** A register member's age today, or null when no date of birth is recorded. */
const ageOf = (m: Member): number | null => {
  const a = ageFrom(m.dateOfBirth, '');
  return a === '' ? null : Number(a);
};

/** Whether a register member falls inside a fellowship's rule. */
const fitsRule = (g: Rule, m: Member): boolean => {
  const wants = g.gender && g.gender !== 'Both' ? g.gender : null;
  if (wants && m.gender !== wants) return false;
  const from = g.ageFrom ?? null; const to = g.ageTo ?? null;
  if (from === null && to === null) return true;
  const age = ageOf(m);
  if (age === null) return false;
  return (from === null || age >= from) && (to === null || age <= to);
};

const YouthFellowship = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  const navigate = useNavigate();

  // The Summary tab reads everything; the rest of the screen works on one tab.
  const [activeView, setActiveView] = useState('summary');
  const isSummary = activeView === 'summary';
  const activeTab = isSummary ? 'groups' : activeView;
  const [viewMode, setViewMode] = useState('history');
  const [groupForm, setGroupForm] = useState(EMPTY_GROUP);
  const [progForm, setProgForm] = useState(EMPTY_PROGRAM);
  const [editingId, setEditingId] = useState(null);
  /** The record open in the detail drawer — a group or a programme, by tab. */
  const [viewing, setViewing] = useState(null);
  /** A fellowship whose roll is being printed / saved as PDF. */
  const [printGroup, setPrintGroup] = useState(null);

  const isEditing = editingId !== null;
  const isGroups = activeTab === 'groups';
  const isDeletedView = viewMode === 'deleted';

  const { notification, notify, dismiss } = useNotification();
  // One record on paper, in the layout chosen under Settings › Print Templates.
  const { print, printing } = usePrint(notify);

  const ledger = useMultiLedger({
    groups: { resource: youthGroupsApi, label: 'Fellowship', prefix: 'YFG' },
    programs: { resource: youthProgramsApi, label: 'Program', prefix: 'YPR' }
  }, { notify });

  const fellowships = useOptionList('church_yf_fellowships');
  const progTypes = useOptionList('church_yf_prog_types');

  // The people: every live, living member of the church register.
  const { members: register, loading: registerLoading } = useMemberDirectory();
  const people = useMemo(
    () => register.filter((m) => (m.status || 'Alive') === 'Alive'),
    [register]
  );

  const groups = ledger.active('groups');
  const peopleIn = (g: Rule) => people.filter((m) => fitsRule(g, m));
  const groupByName = (name?: string) =>
    groups.find((g) => String(g.name || '').trim().toLowerCase() === String(name || '').trim().toLowerCase());
  // Programmes are filed under a fellowship; offer the ones set up here.
  const fellowshipOptions = groups.length ? groups.map((g) => String(g.name || '')) : fellowships.options;

  const groupKey = isGroups ? 'groups' : 'programs';
  const dataset = isDeletedView ? ledger.voided(groupKey) : ledger.active(groupKey);

  // Programmes carry a date; groups have none and are never filtered out.
  const range = useDateRangeFilter(dataset);
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    range.filtered, ['recordId', 'name', 'fellowshipName', 'title', 'notes', 'progType']
  );
  const page = usePagination(filtered);

  const resetForms = () => {
    setGroupForm(EMPTY_GROUP);
    setProgForm(EMPTY_PROGRAM);
    setEditingId(null);
  };

  const switchTab = (tab) => {
    setViewing(null);
    setActiveView(tab);
    setViewMode('history');
    resetForms();
    page.reset();
    clear();
    range.clear();
  };

  const switchView = (mode) => {
    setViewing(null);
    if (mode !== 'add') resetForms();
    setViewMode(mode);
    page.reset();
  };

  // "Open the record" from the Approvals inbox: its tab (Deleted when it is
  // gone), then its view.
  useOpenRecordFromLink(({ id, ref }) => {
    const row = findLedgerRow(ledger.rows, id, ref);
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    const key = row.__key as 'groups' | 'programs';
    switchTab(key);
    if ((ledger.voided(key) as unknown[]).includes(row)) setViewMode('deleted');
    setViewing(row);
  }, !ledger.loading);

  const openMember = (m: Member) => navigate(pathForModuleId('Members & Families'), { state: { viewMemberId: m.id } });

  // --- Fellowship groups ---------------------------------------------------

  const editGroup = (item) => {
    setGroupForm({
      name: item.name || '', ageFrom: item.ageFrom ?? '', ageTo: item.ageTo ?? '',
      gender: item.gender || 'Both', notes: item.notes || ''
    });
    setEditingId(item.rawId);
    setViewMode('add');
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    const name = groupForm.name.trim();
    if (!name) { notify('Select or type the fellowship name.', 'error'); return; }
    const clash = groups.some((g) => g.rawId !== editingId && String(g.name || '').trim().toLowerCase() === name.toLowerCase());
    if (clash) { notify(`A fellowship called "${name}" already exists.`, 'error'); return; }
    const ageFromN = groupForm.ageFrom === '' ? null : Number(groupForm.ageFrom);
    const ageToN = groupForm.ageTo === '' ? null : Number(groupForm.ageTo);
    if ((ageFromN !== null && !(ageFromN >= 0)) || (ageToN !== null && !(ageToN >= 0))) { notify('Ages cannot be negative.', 'error'); return; }
    if (ageFromN !== null && ageToN !== null && ageToN < ageFromN) { notify('"Age to" cannot be less than "Age from".', 'error'); return; }

    fellowships.ensure(name);
    const existing = isEditing ? groups.find((g) => g.rawId === editingId) : null;
    const payload = {
      name, ageFrom: ageFromN, ageTo: ageToN, gender: groupForm.gender, notes: groupForm.notes,
      recordId: existing?.recordId || ledger.nextSequence('groups'),
      status: 'ACTIVE' as const
    };
    if (await ledger.save('groups', payload, editingId)) {
      resetForms();
      setViewMode('history');
    }
  };

  // --- Programmes ----------------------------------------------------------

  const editProgram = (item) => {
    setProgForm({
      fellowshipName: item.fellowshipName || FELLOWSHIP_FALLBACK,
      date: item.date || '', title: item.title || '', progType: item.progType || '',
      attendance: item.attendance || '', budget: item.budget || '', notes: item.notes || ''
    });
    setEditingId(item.rawId);
    setViewMode('add');
  };

  const handleProgramSubmit = async (e) => {
    e.preventDefault();

    const fellowshipName = progForm.fellowshipName.trim();
    const progType = progForm.progType.trim();
    const missing = missingRequired([[fellowshipName, 'Fellowship Name'], [progType, 'Program Type']]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }
    fellowships.ensure(fellowshipName);
    progTypes.ensure(progType);

    const existing = isEditing ? ledger.active('programs').find((p) => p.rawId === editingId) : null;
    const payload = {
      ...progForm,
      fellowshipName,
      progType,
      budget: progForm.budget ? parseFloat(progForm.budget) : 0.0,
      recordId: existing?.recordId || ledger.nextSequence('programs'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('programs', payload, editingId)) {
      resetForms();
      setViewMode('history');
    }
  };

  // --- Tables --------------------------------------------------------------

  const idColumn = { key: 'recordId', header: 'Record ID', nowrap: true, render: (r) => <strong>{r.recordId}</strong> };
  const sub = { fontSize: '11px', color: t.textFaint };

  const groupColumns = [
    idColumn,
    { key: 'name', header: 'Fellowship', render: (r) => <strong style={{ color: t.textAccent, fontSize: '15px' }}>{r.name}</strong> },
    { key: 'age', header: 'Age', nowrap: true, render: (r) => ageBandOf(r) },
    { key: 'gender', header: 'Gender', nowrap: true, render: (r) => <Badge tone={r.gender === 'Female' ? 'accent' : r.gender === 'Male' ? 'neutral' : 'success'}>{r.gender || 'Both'}</Badge> },
    {
      key: 'people',
      header: 'Members',
      align: 'center' as const,
      render: (r) => (
        <>
          <strong style={{ fontSize: '16px' }}>{registerLoading ? '…' : peopleIn(r).length}</strong>
          <div style={sub}>from the register</div>
        </>
      )
    },
    { key: 'notes', header: 'Remark', wrap: true, render: (r) => r.notes || '-' }
  ];

  const programColumns = [
    idColumn,
    {
      key: 'fellowshipName',
      header: 'Fellowship',
      render: (r) => <span style={{ color: t.textAccent, fontWeight: 'bold' }}>{r.fellowshipName || FELLOWSHIP_FALLBACK}</span>
    },
    { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
    { key: 'title', header: 'Event Title', render: (r) => <strong>{r.title}</strong> },
    { key: 'progType', header: 'Category', render: (r) => <Badge>{r.progType}</Badge> },
    { key: 'attendance', header: 'Attendance', align: 'center' as const, render: (r) => r.attendance || '-' },
    { key: 'notes', header: 'Notes', wrap: true, render: (r) => r.notes || '-' },
    {
      key: 'budget',
      header: 'Budget Used',
      nowrap: true,
      render: (r) => <span style={{ color: t.expense, fontWeight: 'bold' }}>{formatCurrency(r.budget)}</span>
    }
  ];

  const tailColumns = isDeletedView
    ? [
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={sub}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> }
      ]
    : [
        {
          key: 'actions',
          header: 'Actions',
          align: 'right' as const,
          nowrap: true,
          render: (r) => (
            <span onClick={(e) => e.stopPropagation()}>
              <RowActions
                isDarkMode={isDarkMode}
                items={[
                  { icon: '👁️', label: 'View', onClick: () => setViewing(r) },
                  { icon: '🖨️', label: 'Print', disabled: printing, onClick: () => printRecord(r) },
                  { icon: '✏️', label: 'Edit', write: true, onClick: () => (isGroups ? editGroup(r) : editProgram(r)) },
                  { icon: '🗑️', label: 'Delete', separated: true, danger: true, write: true, onClick: () => ledger.requestVoid(r) }
                ]}
              />
            </span>
          )
        }
      ];

  const columns = [
    ...(isGroups ? groupColumns : programColumns),
    { key: 'createdBy', header: 'Created By', render: (r) => (r as { createdBy?: string }).createdBy || '-' },
    ...tailColumns
  ];

  // --- Printing one record ---------------------------------------------------

  /** A fellowship with its rule and everyone in the register who fits it. */
  const fellowshipPrintData = (g) => {
    const roll = [...peopleIn(g)].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return {
      recordId: g.recordId, name: g.name, ageBand: ageBandOf(g),
      ageFrom: g.ageFrom ?? '', ageTo: g.ageTo ?? '', gender: g.gender || 'Both',
      notes: g.notes || '', createdBy: g.createdBy || '',
      memberCount: roll.length, rowCount: roll.length,
      rows: roll.map((m, i) => ({
        index: i + 1, name: m.name, memberId: m.memberId || '', age: ageFrom(m.dateOfBirth, ''),
        gender: m.gender || '', dateOfBirth: formatDate(m.dateOfBirth, ''), mobileNumber: m.mobileNumber || '',
        familyId: m.familyId || '', memberStatus: m.memberStatus || ''
      }))
    };
  };

  const programPrintData = (p) => ({
    recordId: p.recordId, title: p.title, date: formatDate(p.date, ''),
    fellowshipName: p.fellowshipName || FELLOWSHIP_FALLBACK, progType: p.progType || '',
    attendance: p.attendance || '', budget: formatCurrency(p.budget), notes: p.notes || '',
    createdBy: p.createdBy || ''
  });

  const printRecord = (r) => (isGroups
    ? print({
      module: 'Youth Fellowship', documentType: 'YOUTH_FELLOWSHIP_RECORD',
      data: fellowshipPrintData(r), title: `Fellowship-${r.recordId}`
    })
    : print({
      module: 'Youth Fellowship', documentType: 'YOUTH_PROGRAM_RECORD',
      data: programPrintData(r), title: `Programme-${r.recordId}`
    }));

  const previewRule: Rule = {
    ageFrom: groupForm.ageFrom === '' ? null : Number(groupForm.ageFrom),
    ageTo: groupForm.ageTo === '' ? null : Number(groupForm.ageTo),
    gender: groupForm.gender
  };
  const previewCount = peopleIn(previewRule).length;

  // --- Print preview: one fellowship's rule and roll, on the parish's paper ---
  if (printGroup) {
    const g = printGroup;
    const roll = [...peopleIn(g)].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    const cell = { padding: '7px 10px', borderBottom: '1px solid #ddd', fontSize: '13px' };
    const head = { ...cell, fontWeight: 'bold', borderBottom: '2px solid #333', textAlign: 'left' as const };
    return (
      <div style={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '20px' }}>
          <Button variant="secondary" onClick={() => setPrintGroup(null)}>← Back to Fellowships</Button>
        </div>
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '18px' }}>
            <tbody>
              {[
                ['Fellowship', g.name],
                ['Age', ageBandOf(g)],
                ['Gender', g.gender || 'Both'],
                ['Members', String(roll.length)],
                ['Remark', g.notes || '-'],
                ['Printed on', formatDate(new Date().toISOString().slice(0, 10))]
              ].map(([label, value]) => (
                <tr key={label}>
                  <td style={{ ...cell, fontWeight: 'bold', width: '30%' }}>{label}</td>
                  <td style={cell}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...head, width: '36px' }}>#</th>
                <th style={head}>Name</th>
                <th style={head}>Member ID</th>
                <th style={{ ...head, textAlign: 'center' }}>Age</th>
                <th style={head}>Gender</th>
                <th style={head}>Mobile</th>
                <th style={head}>Family</th>
                <th style={{ ...head, width: '90px' }}>Signature</th>
              </tr>
            </thead>
            <tbody>
              {roll.length === 0 ? (
                <tr><td colSpan={8} style={{ ...cell, textAlign: 'center', color: '#777' }}>Nobody in the register fits this rule yet.</td></tr>
              ) : roll.map((m, i) => (
                <tr key={m.id}>
                  <td style={cell}>{i + 1}</td>
                  <td style={{ ...cell, fontWeight: 'bold' }}>{m.name}</td>
                  <td style={cell}>{m.memberId || '-'}</td>
                  <td style={{ ...cell, textAlign: 'center' }}>{ageFrom(m.dateOfBirth)}</td>
                  <td style={cell}>{m.gender || '-'}</td>
                  <td style={cell}>{m.mobileNumber || '-'}</td>
                  <td style={cell}>{m.familyId || '-'}</td>
                  <td style={cell} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="🔥"
        title="Youth Fellowship Management"
        activeTab={activeView}
        onTabChange={switchTab}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'groups', label: `🏷️ Fellowships (${groups.length})` },
          { key: 'programs', label: '📅 Programs & Events' }
        ]}
      />

      {isSummary && (
        <YouthSummary
          groups={groups}
          people={people}
          programs={ledger.active('programs')}
          loading={ledger.loading || registerLoading}
          isDarkMode={isDarkMode}
        />
      )}

      {!isSummary && (
      <SubTabs
        value={viewMode}
        onChange={switchView}
        tabs={[
          { key: 'history', label: 'Active records' },
          { key: 'deleted', label: 'Deleted', variant: 'danger' },
          { key: 'add', write: true, label: isEditing ? '✎ Editing record…' : (isGroups ? '+ Create fellowship' : '+ Add program') }
        ]}
      />
      )}

      {!isSummary && (viewMode === 'history' || isDeletedView) && (
        <>
          {isGroups && !isDeletedView && (
            <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: `0 0 ${t.spacing.md}` }}>
              Each fellowship is a rule — an age band and a gender. Its members are the people in
              <strong> Members &amp; Families</strong> who fit that rule, so the list is always current.
            </p>
          )}
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder={isGroups ? 'Search fellowships...' : 'Search by Title, Fellowship, Type or ID...'}
            actions={(
              <>
                {!isGroups && <DateRangeFilter {...range} onChange={page.reset} />}
                  <PrintButton
                    module="Youth Fellowship"
                    documentType={((activeTab === 'programs' ? { doc: 'YOUTH_PROGRAM_REGISTER', title: 'Youth Programmes & Events' } : { doc: 'YOUTH_REGISTER', title: 'Youth Fellowships' })).doc}
                    size="sm"
                    title={((activeTab === 'programs' ? { doc: 'YOUTH_PROGRAM_REGISTER', title: 'Youth Programmes & Events' } : { doc: 'YOUTH_REGISTER', title: 'Youth Fellowships' })).title.replace(/[^A-Za-z0-9]+/g, '-')}
                    // Each tab prints its own register, with every row that matches the
                    // filters — dates formatted and Created By on every line.
                    data={() => ({
                      title: `${((activeTab === 'programs' ? { doc: 'YOUTH_PROGRAM_REGISTER', title: 'Youth Programmes & Events' } : { doc: 'YOUTH_REGISTER', title: 'Youth Fellowships' })).title}${isDeletedView ? ' (Deleted)' : ''}`,
                      reference: printReference(searchTerm && `Search: "${searchTerm}"`),
                      rowCount: filtered.length,
                      rows: printableRows(filtered)
                    })}
                    onNotify={notify}
                    isDarkMode={isDarkMode}
                  />
              </>
            )}
          />

          <TablePanel
            title={<>{isGroups ? 'Fellowship Register' : 'Programs & Events'}</>}
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />}
              columns={columns}
              rows={page.currentItems}
              rowKey="rawId"
              onRowClick={(r) => setViewing(r)}
              loading={ledger.loading}
              emptyMessage={isGroups ? 'No fellowship set up yet. Create one with its age band and gender.' : 'No programs recorded for this fellowship yet.'}
          />
        </>
      )}

      {/* Fellowship view: the rule, and everyone in the register who fits it. */}
      {!isSummary && viewing && isGroups && (
        <Drawer
          open
          width="680px"
          title={viewing.name}
          subtitle={`${viewing.recordId} · ${ruleOf(viewing)}`}
          onClose={() => setViewing(null)}
          isDarkMode={isDarkMode}
          footer={
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button disabled={printing || registerLoading} onClick={() => printRecord(viewing)}>🖨️ Print / Save PDF</Button>
              {!isDeletedView && (
                <Button write variant="secondary" onClick={() => { const r = viewing; setViewing(null); editGroup(r); }}>✏️ Edit Fellowship</Button>
              )}
            </div>
          }
        >
          {(() => {
            const list = [...peopleIn(viewing)].sort((a, b) => String(a.name).localeCompare(String(b.name)));
            return (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
                  {[
                    { label: 'Age', value: ageBandOf(viewing) },
                    { label: 'Gender', value: viewing.gender || 'Both' },
                    { label: 'Members', value: registerLoading ? '…' : String(list.length) }
                  ].map((box) => (
                    <div key={box.label} style={{ backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`, borderRadius: t.radius.lg, padding: '10px 12px' }}>
                      <div style={{ fontSize: '11px', color: t.textMuted, fontWeight: 'bold', textTransform: 'uppercase' }}>{box.label}</div>
                      <div style={{ fontSize: '18px', fontWeight: 900, color: t.textMain }}>{box.value}</div>
                    </div>
                  ))}
                </div>

                {viewing.notes && (
                  <>
                    <Drawer.Section title="Remark" isDarkMode={isDarkMode} />
                    <div style={{ color: t.textMain, marginBottom: '8px', whiteSpace: 'pre-wrap' }}>{viewing.notes}</div>
                  </>
                )}

                <Drawer.Section title={`Members (${list.length}) — from Members & Families`} isDarkMode={isDarkMode} />
                {registerLoading ? (
                  <div style={{ color: t.textFaint, padding: '10px 0' }}>Loading the register…</div>
                ) : list.length === 0 ? (
                  <div style={{ color: t.textFaint, padding: '10px 0' }}>
                    Nobody in the register fits this rule yet. Members need a date of birth and gender to be counted.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', color: t.textMain, fontSize: t.fontSize.md }}>
                    <thead>
                      <tr style={{ color: t.textMuted, fontSize: '11px', textTransform: 'uppercase' }}>
                        <th style={{ textAlign: 'left', padding: '6px 4px', borderBottom: `1px solid ${t.border}` }}>Name</th>
                        <th style={{ textAlign: 'center', padding: '6px 4px', borderBottom: `1px solid ${t.border}` }}>Age</th>
                        <th style={{ textAlign: 'left', padding: '6px 4px', borderBottom: `1px solid ${t.border}` }}>Gender</th>
                        <th style={{ textAlign: 'left', padding: '6px 4px', borderBottom: `1px solid ${t.border}` }}>Mobile</th>
                        <th style={{ textAlign: 'left', padding: '6px 4px', borderBottom: `1px solid ${t.border}` }}>Family</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((m) => (
                        <tr key={m.id}>
                          <td style={{ padding: '6px 4px', borderBottom: `1px solid ${t.border}` }}>
                            <button type="button" onClick={() => openMember(m)} title="Open in Members & Families"
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: t.textAccent, fontWeight: 'bold', font: 'inherit', textAlign: 'left' }}>
                              {m.name}
                            </button>
                            <div style={sub}>{m.memberId}</div>
                          </td>
                          <td style={{ padding: '6px 4px', textAlign: 'center', borderBottom: `1px solid ${t.border}` }}>{ageFrom(m.dateOfBirth)}</td>
                          <td style={{ padding: '6px 4px', borderBottom: `1px solid ${t.border}` }}>{m.gender || '-'}</td>
                          <td style={{ padding: '6px 4px', borderBottom: `1px solid ${t.border}` }}>{m.mobileNumber || '-'}</td>
                          <td style={{ padding: '6px 4px', borderBottom: `1px solid ${t.border}` }}>{m.familyId || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <Drawer.Section title="Record" isDarkMode={isDarkMode} />
                <Drawer.Row label="Record ID" value={viewing.recordId} isDarkMode={isDarkMode} />
                {viewing.createdBy && <Drawer.Row label="Created By" value={viewing.createdBy} isDarkMode={isDarkMode} />}
                {isDeletedView && (
                  <>
                    <Drawer.Row label="Deleted On" value={viewing.deletedAt} isDarkMode={isDarkMode} />
                    <Drawer.Row label="Reason" value={viewing.deleteReason} isDarkMode={isDarkMode} />
                  </>
                )}
              </>
            );
          })()}
        </Drawer>
      )}

      {/* Programme view */}
      {!isSummary && viewing && !isGroups && (
        <Drawer
          open
          title={viewing.title}
          subtitle={`${viewing.recordId} · ${formatDate(viewing.date)}`}
          onClose={() => setViewing(null)}
          isDarkMode={isDarkMode}
          footer={
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button disabled={printing} onClick={() => printRecord(viewing)}>🖨️ Print / Save PDF</Button>
              {!isDeletedView && (
                <Button write variant="secondary" onClick={() => { const r = viewing; setViewing(null); editProgram(r); }}>✏️ Edit Program</Button>
              )}
            </div>
          }
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <Badge tone="accent">{viewing.fellowshipName || FELLOWSHIP_FALLBACK}</Badge>
            {viewing.progType && <Badge>{viewing.progType}</Badge>}
          </div>

          <Drawer.Section title="Program" isDarkMode={isDarkMode} />
          <Drawer.Row label="Title" value={viewing.title} isDarkMode={isDarkMode} />
          <Drawer.Row label="Date" value={formatDate(viewing.date)} isDarkMode={isDarkMode} />
          <Drawer.Row label="Category" value={viewing.progType} isDarkMode={isDarkMode} />
          <Drawer.Row label="Fellowship" isDarkMode={isDarkMode}>
            {(() => {
              const g = groupByName(viewing.fellowshipName);
              return (
                <>
                  <strong style={{ color: t.textMain }}>{viewing.fellowshipName || FELLOWSHIP_FALLBACK}</strong>
                  {g && <div style={{ fontSize: '12px', color: t.textMuted }}>{ruleOf(g)} · {peopleIn(g).length} members</div>}
                </>
              );
            })()}
          </Drawer.Row>

          <Drawer.Section title="Outcome" isDarkMode={isDarkMode} />
          <Drawer.Row label="Attendance" isDarkMode={isDarkMode}>
            <strong style={{ fontSize: '18px', color: t.textMain }}>{viewing.attendance || '-'}</strong>
          </Drawer.Row>
          <Drawer.Row label="Budget Used" isDarkMode={isDarkMode}>
            <strong style={{ fontSize: '18px', color: t.expense }}>{formatCurrency(viewing.budget)}</strong>
          </Drawer.Row>
          <Drawer.Row label="Notes" value={viewing.notes} isDarkMode={isDarkMode} />

          <Drawer.Section title="Record" isDarkMode={isDarkMode} />
          <Drawer.Row label="Record ID" value={viewing.recordId} isDarkMode={isDarkMode} />
          {viewing.createdBy && <Drawer.Row label="Created By" value={viewing.createdBy} isDarkMode={isDarkMode} />}
          {isDeletedView && (
            <>
              <Drawer.Row label="Deleted On" value={viewing.deletedAt} isDarkMode={isDarkMode} />
              <Drawer.Row label="Reason" value={viewing.deleteReason} isDarkMode={isDarkMode} />
            </>
          )}
        </Drawer>
      )}

      {/* Fellowship form: the name and the rule, nothing else. */}
      {!isSummary && viewMode === 'add' && isGroups && (
        <FormLayout
          title={isEditing ? 'Edit Fellowship' : 'Create Fellowship'}
          submitLabel={isEditing ? 'Update Fellowship' : 'Save Fellowship'}
          submitting={ledger.saving}
          onSubmit={handleGroupSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
          maxWidth="640px"
        >
          <FormLayout.Grid>
            <SearchableSelect
              label="Select Fellowship Name" required fullWidth
              placeholder="e.g. Youth Fellowship, Women's Fellowship..."
              value={groupForm.name}
              onChange={(v) => setGroupForm((prev) => ({ ...prev, name: v }))}
              options={fellowships.options}
              onOptionsChange={fellowships.setOptions}
            />
            <Input
              label="Age from" type="number" min={0} max={120} step={1} placeholder="any"
              value={groupForm.ageFrom}
              onChange={(e) => setGroupForm({ ...groupForm, ageFrom: e.target.value })}
            />
            <Input
              label="Age to" type="number" min={groupForm.ageFrom || 0} max={120} step={1} placeholder="any"
              value={groupForm.ageTo}
              onChange={(e) => setGroupForm({ ...groupForm, ageTo: e.target.value })}
            />
            <Select
              label="Gender" placeholder="" required fullWidth
              options={GROUP_GENDERS}
              value={groupForm.gender}
              onChange={(e) => setGroupForm({ ...groupForm, gender: e.target.value })}
            />
            <Input
              label="Remark" type="textarea" rows={2} fullWidth
              placeholder="Purpose, meeting day, leader..."
              value={groupForm.notes}
              onChange={(e) => setGroupForm({ ...groupForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
          {/* Who this rule picks up right now, before it is saved. */}
          <div style={{
            margin: '4px 0 12px', padding: '10px 14px', borderRadius: t.radius.lg,
            backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`, color: t.textMain, fontSize: t.fontSize.md
          }}>
            Rule: <strong>{ruleOf(previewRule)}</strong> — currently matches{' '}
            <strong>{registerLoading ? '…' : previewCount}</strong> {previewCount === 1 ? 'person' : 'people'} in Members &amp; Families.
          </div>
        </FormLayout>
      )}

      {/* Programme form */}
      {!isSummary && viewMode === 'add' && !isGroups && (
        <FormLayout
          title={isEditing ? 'Edit Program' : 'Schedule Youth Program'}
          submitLabel={isEditing ? 'Update Program' : 'Save Program Log'}
          submitting={ledger.saving}
          onSubmit={handleProgramSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <FormLayout.Grid>
            <SearchableSelect
              label="Select Fellowship Name" required
              placeholder="e.g. Youth Fellowship, Women's Fellowship..."
              value={progForm.fellowshipName}
              onChange={(v) => setProgForm((prev) => ({ ...prev, fellowshipName: v }))}
              options={fellowshipOptions}
              allowAdd={!groups.length} allowManage={false}
            />
            <Input
              label="Program / Event Name" required fullWidth placeholder="e.g. Summer Youth Camp"
              value={progForm.title}
              onChange={(e) => setProgForm({ ...progForm, title: e.target.value })}
              style={{ fontSize: '16px', fontWeight: 'bold' }}
            />
            <Input
              label="Event Date" type="date" required
              value={progForm.date}
              onChange={(e) => setProgForm({ ...progForm, date: e.target.value })}
            />
            <SearchableSelect
              label="Program Type" required placeholder="Search or type type..."
              value={progForm.progType}
              onChange={(v) => setProgForm((prev) => ({ ...prev, progType: v }))}
              options={progTypes.options}
              onOptionsChange={progTypes.setOptions}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <Input
                label="Total Attendance" type="number" placeholder="Headcount" containerStyle={{ flex: 1 }}
                value={progForm.attendance}
                onChange={(e) => setProgForm({ ...progForm, attendance: e.target.value })}
              />
              <Input
                label="Budget Used (₹)" type="number" placeholder="0.00" containerStyle={{ flex: 1 }}
                value={progForm.budget}
                onChange={(e) => setProgForm({ ...progForm, budget: e.target.value })}
                style={{ color: t.expense, fontWeight: 'bold' }}
              />
            </div>
            <Input
              label="Notes / Impact" type="textarea" rows={2} fullWidth
              placeholder="Guest speakers, highlights..."
              value={progForm.notes}
              onChange={(e) => setProgForm({ ...progForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      <PromptDialog
        open={Boolean(ledger.voiding)}
        title={`Delete ${ledger.voiding?.recordType || ''} ${ledger.voiding?.recordId || ''}`}
        message="Please enter the reason for removing this record. It will be kept in Deleted History."
        placeholder="Reason for voiding"
        confirmLabel="Delete"
        requiredError="A reason is required to remove a record."
        onSubmit={(reason) => ledger.confirmVoid(reason)}
        onCancel={ledger.cancelVoid}
      />
    </div>
  );
};

export default YouthFellowship;
